import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Scan, Play, CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const statusPill = {
  queued:    "text-gray-800 border-gray-300",
  running:   "text-[#E3000B] border-[#E3000B]/40 bg-[#E3000B]/10",
  completed: "text-[#00A850] border-[#00A850]/40 bg-[#00A850]/10",
  failed:    "text-[#FF3333] border-[#FF3333]/40 bg-[#FF3333]/10",
};

export default function Scans() {
  const [scans, setScans] = useState([]);
  const [themes, setThemes] = useState([]);
  const [kind, setKind] = useState("all");
  const [theme, setTheme] = useState("");
  const [setNumbers, setSetNumbers] = useState("");
  const [live, setLive] = useState(false);
  const [asinOnly, setAsinOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prevPreview, setPrevPreview] = useState(null);

  const load = async () => {
    const s = await api.listScans();
    setScans(s);
  };

  useEffect(() => {
    load();
    api.themes().then(setThemes);
    const interval = setInterval(load, 3000);
    return () => clearInterval(interval);
  }, []);

  // Lazy-load the archive count when user picks the "prev" kind
  useEffect(() => {
    if (kind !== "prev" || prevPreview) return;
    api.previewPreviouslyPurchasedScan().then(setPrevPreview).catch(() => {});
  }, [kind, prevPreview]);

  const startScan = async () => {
    setBusy(true);
    try {
      if (kind === "prev") {
        await api.scanPreviouslyPurchased(live, false);
        toast.success(`Queued live refresh of ${prevPreview?.count ?? "?"} previously-purchased sets`);
      } else if (asinOnly && (kind === "theme" || kind === "set_list" || kind === "all")) {
        // ASIN-only path — reuse the ASIN Resolver, scoped by theme or explicit set_numbers.
        const nums = setNumbers.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
        const body = {
          continuous: true,
          fetch_bricklink: false,
          skip_attempted: true,
          retry_failed: false,
          limit: 5000,
        };
        if (kind === "theme") {
          if (!theme) { toast.error("Pick a theme first"); setBusy(false); return; }
          body.theme = theme;
        } else if (kind === "set_list") {
          if (nums.length === 0) { toast.error("Paste at least one set number"); setBusy(false); return; }
          body.set_numbers = nums;
          body.limit = nums.length;
        }
        // kind === "all": no theme, no set_numbers → resolver walks entire catalog
        await api.asinResolverRun(body);
        toast.success(`ASIN resolver queued — track progress here`);
      } else {
        const nums = setNumbers.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
        const req = { kind, live };
        if (kind === "theme") req.theme = theme;
        if (kind === "set_list") req.set_numbers = nums;
        await api.createScan(req);
        toast.success("Scan queued");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to queue scan");
    }
    setBusy(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Scan size={22} weight="bold" className="text-[#E3000B]" />
        <h1 className="font-head text-2xl font-black uppercase tracking-tighter" data-testid="scans-title">Scan Engine</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 border border-[#E4E4E0] rounded-sm p-4 space-y-3">
          <div className="font-head font-bold uppercase text-xs tracking-wider text-gray-600 mb-2">New Scan</div>

          <div>
            <Label className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Scan Type</Label>
            <div className="grid grid-cols-4 gap-1 mt-1">
              {[
                { k: "all", l: "ALL" },
                { k: "theme", l: "THEME" },
                { k: "set_list", l: "LIST" },
                { k: "prev", l: "PREV" },
              ].map(o => (
                <button key={o.k} onClick={() => setKind(o.k)}
                  data-testid={`scan-kind-${o.k}`}
                  className={`h-8 border text-xs font-mono font-bold uppercase rounded-sm ${kind === o.k ? "border-[#E3000B] bg-[#E3000B]/10 text-[#E3000B]" : "border-[#E4E4E0] text-gray-600 hover:border-zinc-500"}`}>
                  {o.l}
                </button>
              ))}
            </div>
            {kind === "prev" && (
              <div className="text-[10px] font-mono text-gray-500 leading-relaxed mt-2">
                Refreshes today&apos;s Amazon buy box + BrickLink price for every set you&apos;ve ever
                purchased (Active + Archive). Bounded token cost — no full-catalog scan.
              </div>
            )}
          </div>

          {kind === "prev" && (
            <div className="border border-[#E4E4E0] bg-[#F6F6F3] rounded-sm p-3 space-y-1" data-testid="scan-prev-preview">
              {prevPreview ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Sets To Scan</span>
                    <span className="font-head text-xl font-black text-[#E3000B]">{prevPreview.count}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] font-mono text-gray-500">
                    <span>Active: <span className="text-gray-800">{prevPreview.active_count}</span></span>
                    <span>Archive: <span className="text-gray-800">{prevPreview.archive_count}</span></span>
                  </div>
                  {prevPreview.count === 0 && (
                    <div className="text-[10px] font-mono text-[#FF8C00] pt-1 border-t border-[#E4E4E0]">
                      No deals uploaded yet — head to the Deals page and upload a CSV first.
                    </div>
                  )}
                </>
              ) : (
                <div className="text-[10px] font-mono text-gray-500">Loading archive count…</div>
              )}
            </div>
          )}

          {kind === "theme" && (
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Theme</Label>
              <select value={theme} onChange={e => setTheme(e.target.value)}
                className="w-full h-8 bg-[#F6F6F3] border border-[#E4E4E0] text-xs font-mono px-2 text-gray-800 rounded-sm"
                data-testid="scan-theme-select">
                <option value="">-- select --</option>
                {themes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          {kind === "set_list" && (
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Set Numbers (paste / CSV)</Label>
              <Textarea value={setNumbers} onChange={e => setSetNumbers(e.target.value)} rows={5}
                placeholder="75192, 10276, 71043…"
                className="bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs rounded-sm" data-testid="scan-set-list" />
            </div>
          )}

          <div className="flex items-center justify-between border-t border-[#E4E4E0] pt-3">
            <div>
              <div className="text-xs font-mono text-gray-900">Live Mode</div>
              <div className="text-[10px] font-mono text-gray-500">Query Keepa + BrickLink</div>
            </div>
            <Switch checked={live} onCheckedChange={setLive} className="data-[state=checked]:bg-[#E3000B]" data-testid="scan-live-toggle" />
          </div>

          {kind !== "prev" && (
            <div className="flex items-center justify-between border border-[#D97706]/40 bg-[#D97706]/[0.04] rounded-sm p-2">
              <div>
                <div className="text-xs font-mono text-[#D97706]">ASIN Only</div>
                <div className="text-[10px] font-mono text-gray-500 leading-relaxed">
                  Resolve missing Amazon ASINs — skips price fetch, ~5× cheaper on Keepa tokens.
                  Uses the ASIN Resolver in Continuous mode.
                </div>
              </div>
              <Switch checked={asinOnly} onCheckedChange={setAsinOnly}
                className="data-[state=checked]:bg-[#D97706]" data-testid="scan-asin-only-toggle" />
            </div>
          )}

          <Button
            onClick={startScan}
            disabled={busy || (kind === "prev" && prevPreview?.count === 0)}
            className={`w-full h-9 rounded-sm font-bold uppercase tracking-wider disabled:opacity-50 ${asinOnly && kind !== "prev" ? "bg-[#D97706] text-gray-900 hover:bg-[#FFAB1F]" : "bg-[#E3000B] text-white hover:bg-[#E6B800]"}`}
            data-testid="scan-start-btn">
            <Play size={14} weight="fill" className="mr-1" /> {busy ? "QUEUING…" : (asinOnly && kind !== "prev" ? "RUN ASIN SCAN" : "RUN SCAN")}
          </Button>
        </div>

        <div className="lg:col-span-2 border border-[#E4E4E0] rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E4E4E0] font-head font-bold uppercase text-xs tracking-wider text-gray-600">Scan History</div>
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs font-mono">
              <thead className="bg-[#F6F6F3] border-b border-[#E4E4E0]">
                <tr>
                  <Th>TYPE</Th>
                  <Th>STATUS</Th>
                  <Th>PROGRESS</Th>
                  <Th>FOUND</Th>
                  <Th>MODE</Th>
                  <Th>STARTED</Th>
                </tr>
              </thead>
              <tbody>
                {scans.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-gray-500" data-testid="scans-empty">No scans yet.</td></tr>
                )}
                {scans.map(s => {
                  const pct = s.total ? Math.round((s.progress / s.total) * 100) : 0;
                  const Icon = s.status === "running" ? Spinner : s.status === "completed" ? CheckCircle : s.status === "failed" ? XCircle : Play;
                  return (
                    <tr key={s.id} className="border-b border-[#F6F6F3] hover:bg-[#F6F6F3]" data-testid={`scan-row-${s.id}`}>
                      <td className="px-3 py-2 text-gray-900 uppercase">{s.kind}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 h-5 border rounded-sm text-[10px] uppercase font-bold ${statusPill[s.status] || ""}`}>
                          <Icon size={10} className={s.status === "running" ? "animate-spin" : ""} /> {s.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-800">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-[#F6F6F3] border border-[#E4E4E0] rounded-sm overflow-hidden">
                            <div className="h-full bg-[#E3000B]" style={{ width: `${pct}%` }} />
                          </div>
                          <span>{s.progress}/{s.total}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[#00A850] font-bold">{s.opportunities_found || 0}</td>
                      <td className="px-3 py-2 text-gray-600">{s.live ? "LIVE" : "MOCK"}</td>
                      <td className="px-3 py-2 text-gray-500">{s.started_at ? new Date(s.started_at).toLocaleTimeString() : "--"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-gray-500">{children}</th>;
}
